import yaml from "js-yaml";
import { GetStaticProps, GetStaticPaths } from "next";
import { useTranslation } from "next-i18next";
import { serverSideTranslations } from "next-i18next/serverSideTranslations";
import React from "react";

import fs from "fs";
import path from "path";

import { ContactQuestions } from "@components/contact/components/ContactQuestions";
import { CTA } from "@components/CTA";
import Footer from "@components/footer";
import { CaseStudies } from "@components/landing/CaseStudies";
import { HeroCard } from "@components/landing/HeroCard";
import { Referals } from "@components/landing/Referals";
import { SingleBlogPreview } from "@components/landing/SingleBlogPreview/SingleBlogPreview";
import { Specializations } from "@components/landing/Specializations";
import { Subheader } from "@components/landing/Subheader";
import { TechnologiesSection } from "@components/landing/Technologies";
import { TechnologiesBrag } from "@components/landing/TechnologiesBrag";
import { Values } from "@components/landing/Values";
import { Menu } from "@components/menu";
import { PageMetadata } from "@components/PageMetadata";
import { BlogContext, BlogContextInterface } from "@context/BlogContext";
import { useMediaQueryBreakpoint } from "@hooks";
import { getPosts } from "@lib/blog";
import { ContentTemplate } from "@services/ContentTemplate";
import { languageServiceMap, getServiceLanguage } from "@services/languages";

interface ServicePageProps {
  contentTemplate: ContentTemplate;
  metadata: {
    title: string;
  };
  allPosts: any[];
}

interface PageBodyProps {
  contentTemplate: ContentTemplate;
}

const PageBody = ({ contentTemplate }: PageBodyProps) => {
  const { isTablet } = useMediaQueryBreakpoint();
  const { t } = useTranslation();

  return (
    <>
      {!isTablet && <Menu fixed />}

      <HeroCard
        title={contentTemplate.hero.header.split(" ").slice(0, -1).join(" ")}
        subtitle={contentTemplate.hero.header.split(" ").splice(-1)[0]}
        animationPrefix=""
        animationWords={contentTemplate.hero.animatedEndings}
        ctaLinkHref={"/contact"}
        ctaButtonText={contentTemplate.hero.cta}
        ctaButtonTextShort={contentTemplate.hero.cta}
      />

      <Subheader
        className="mt-4 mb-12"
        content={{
          firstLine: contentTemplate.firstParagraph.header,
          secondLine: contentTemplate.firstParagraph.body,
        }}
      />

      <TechnologiesSection
        header={contentTemplate.technologies.header}
        technologies={contentTemplate.technologies.list}
      />

      <TechnologiesBrag
        className="mt-4"
        header={contentTemplate.secondParagraph.header}
        body={contentTemplate.secondParagraph.body}
      />

      <Referals />

      <TechnologiesBrag
        backgroundGradient={false}
        className="mt-12"
        header={contentTemplate.thirdParagraph.header}
        body={contentTemplate.thirdParagraph.body}
      />

      <Values
        values={contentTemplate.features.map((feature: any) => ({
          icon: "/animations/e2e.json",
          title: feature.header,
          body: feature.description,
        }))}
      />

      {contentTemplate.relatedBlog && (
        <SingleBlogPreview postName={contentTemplate.relatedBlog} />
      )}

      <Specializations
        header={contentTemplate.secondFeatures.header}
        subheaderParts={[contentTemplate.secondFeatures.subheader]}
        inlineItems={[]}
        sections={contentTemplate.secondFeatures.groups.map((group: any) => ({
          ctaText: group.ctaText,
          contents: group.items.map((item: any) => ({
            header: item.header,
            body: item.description,
          })),
        }))}
        ctaBaseText={contentTemplate.secondFeatures.ctaText}
      />

      <CaseStudies
        header={t("landing.case_study.header")}
        subheader={t("landing.case_study.subheader")}
      />

      <TechnologiesBrag
        backgroundGradient={false}
        className="mt-16"
        header={contentTemplate.fourthParagraph.header}
        body={contentTemplate.fourthParagraph.body}
      />

      <ContactQuestions
        className="mt-4"
        questions={contentTemplate.faq.map((faq: any) => ({
          question: faq.question,
          answer: faq.response,
        }))}
      />

      <CTA
        className="my-16"
        buttonText={contentTemplate.cta.buttonText}
        header={contentTemplate.cta.header}
        subheader={contentTemplate.cta.subheader}
      />

      <Footer />
    </>
  );
};

const ServicePage = ({
  contentTemplate,
  metadata,
  allPosts,
}: ServicePageProps) => {
  return (
    <>
      <PageMetadata
        title={metadata.title}
        description={contentTemplate.description}
      />

      <BlogContext.Provider value={new BlogContextInterface(allPosts)}>
        <PageBody contentTemplate={contentTemplate} />
      </BlogContext.Provider>
    </>
  );
};

export const getStaticPaths: GetStaticPaths = async () => {
  const servicesDir = path.join(process.cwd(), "services");
  const serviceDirs = fs
    .readdirSync(servicesDir)
    .filter(file => fs.statSync(path.join(servicesDir, file)).isDirectory());

  const paths = serviceDirs.map(slug => ({
    params: { slug },
    locale: getServiceLanguage(slug),
  }));

  return {
    paths,
    fallback: "blocking",
  };
};

export const getStaticProps: GetStaticProps = async ({
  params,
  locale = "en",
}) => {
  const slug = params?.slug as string;

  try {
    const contentLanguage = getServiceLanguage(slug);

    if (contentLanguage !== locale && slug in languageServiceMap) {
      return {
        redirect: {
          destination: `/${locale}/services/${languageServiceMap[slug]}`,
          permanent: false,
        },
      };
    }

    const serviceDir = path.join(process.cwd(), "services", slug);
    const contentPath = path.join(serviceDir, "content.yaml");
    const metaPath = path.join(serviceDir, "meta.yaml");

    const contentFile = fs.readFileSync(contentPath, "utf8");
    const metaFile = fs.readFileSync(metaPath, "utf8");

    const contentTemplate = yaml.load(contentFile) as ContentTemplate;
    const metadata = yaml.load(metaFile) as {
      title: string;
      description: string;
    };

    return {
      props: {
        allPosts: BlogContextInterface.postsToBlogInterfaceData(
          await getPosts(),
          locale,
        ),
        contentTemplate,
        metadata,
        ...(await serverSideTranslations(locale, ["common"])),
      },
    };
  } catch (error) {
    console.error(`Error loading files for slug ${slug}:`, error);
    return {
      notFound: true,
    };
  }
};

export default ServicePage;
